/**
 * Management console copy — Arabic.
 *
 * Arabic is the primary language, not a translation layer: the interface is authored
 * here first and the layout is built for RTL rather than mirrored into it.
 *
 * Standard room-item names stay in English: they are the inventory identifiers the
 * supervisor selected and the hotel's own vocabulary, not translatable UI prose.
 */

export const AR_LOCALE = 'ar-SA-u-nu-latn';
export const BUSINESS_TZ = 'Asia/Riyadh';

/* Latin digits throughout: they are what the team reads on a door and on a report. */
export const arTime = (iso: string): string =>
  new Intl.DateTimeFormat(AR_LOCALE, {
    timeZone: BUSINESS_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));

export const arTimeSeconds = (iso: string): string =>
  new Intl.DateTimeFormat(AR_LOCALE, {
    timeZone: BUSINESS_TZ,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(new Date(iso));

export const arDate = (iso: string): string =>
  new Intl.DateTimeFormat(AR_LOCALE, {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));

export const arDateShort = (iso: string): string =>
  new Intl.DateTimeFormat(AR_LOCALE, {
    timeZone: BUSINESS_TZ,
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));

export const arDateTime = (iso: string): string =>
  new Intl.DateTimeFormat(AR_LOCALE, {
    timeZone: BUSINESS_TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));

export const arWeekday = (iso: string): string =>
  new Intl.DateTimeFormat(AR_LOCALE, { timeZone: BUSINESS_TZ, weekday: 'long' }).format(
    new Date(iso),
  );

export const ar = {
  appName: 'ROOM VERIFY',
  tagline: 'مركز عمليات التفتيش',

  /* navigation */
  navLandmark: 'التنقّل',
  navGroupOperations: 'العمليات',
  navGroupProperty: 'المنشأة',
  navGroupAdmin: 'الإدارة والتدقيق',
  navOverview: 'نظرة عامة',
  navInspections: 'عمليات اليوم',
  navReports: 'أرشيف التقارير',
  navRooms: 'الغرف',
  navSupervisors: 'المشرفون',
  navAudit: 'سجل التدقيق',
  navSettings: 'الإعدادات',
  roleManagement: 'الإدارة',

  /* auth */
  signIn: 'تسجيل الدخول',
  signInSubtitle: 'الدخول مخصّص لحسابات الإدارة.',
  username: 'اسم المستخدم',
  password: 'كلمة المرور',
  usernamePlaceholder: 'اسم المستخدم',
  passwordPlaceholder: 'كلمة المرور',
  signingIn: 'جارٍ تسجيل الدخول…',
  signOut: 'تسجيل الخروج',
  showPassword: 'إظهار كلمة المرور',
  hidePassword: 'إخفاء كلمة المرور',

  /* shell */
  close: 'إغلاق',
  cancel: 'إلغاء',
  backToList: 'رجوع',
  refresh: 'تحديث',
  loading: 'جارٍ التحميل…',
  today: 'اليوم',
  date: 'التاريخ',
  actions: 'إجراءات',
  retry: 'إعادة المحاولة',

  /* overview */
  overviewSubtitle: (weekday: string, date: string) => `${weekday} · ${date}`,
  totalInspections: 'إجمالي عمليات التفتيش',
  inspectionsCaption: 'غرفة تم توثيقها اليوم',
  checkoutRooms: 'غرف المغادرة',
  cleanRooms: 'الغرف النظيفة',
  averageDuration: 'متوسط المدة',
  withMissingItems: 'بها نقص',
  editedReports: 'تقارير مُعدَّلة',
  bySupervisor: 'حسب المشرف',
  todaysActivity: 'نشاط اليوم',
  viewAll: 'عرض الكل',
  exceptions: 'الاستثناءات التشغيلية',
  noExceptions: 'لا توجد استثناءات',
  noExceptionsText: 'كل الغرف المكتملة اليوم بلا نقص وبلا تعديلات إدارية.',

  /* filters */
  filters: 'التصفية',
  activeFilters: 'عوامل التصفية النشطة',
  removeFilter: 'إزالة',
  dateFrom: 'من تاريخ',
  dateTo: 'إلى تاريخ',
  roomNumber: 'رقم الغرفة',
  room: 'غرفة',
  roomNumberPlaceholder: 'رقم الغرفة',
  inspectionType: 'نوع التفتيش',
  supervisor: 'المشرف',
  allTypes: 'كل الأنواع',
  checkout: 'مغادرة',
  clean: 'نظافة',
  allSupervisors: 'كل المشرفين',
  missingItemsFilter: 'الأغراض الناقصة',
  any: 'الكل',
  hasMissing: 'يوجد نقص',
  noMissing: 'مكتملة',
  editedFilter: 'التعديلات',
  editedOnly: 'مُعدَّلة',
  notEdited: 'غير مُعدَّلة',
  applyFilters: 'تطبيق',
  clearFilters: 'مسح الكل',
  resultsCount: (shown: number, total: number) => `${shown} من ${total}`,

  /* list */
  completedAt: 'وقت الإنهاء',
  duration: 'المدة',
  photos: 'الصور',
  edited: 'مُعدَّل',
  missingItemsBadge: 'نقص',
  itemsCompleteShort: 'مكتملة',
  openReport: 'عرض التقرير',
  openPhoto: 'تكبير الصورة',
  edit: 'تعديل',
  noReportsTitle: 'لا توجد تقارير لهذا اليوم',
  noReportsText: 'جرّب تغيير التاريخ أو إزالة بعض عوامل التصفية.',
  noResultsTitle: 'لا توجد نتائج',
  noResultsText: 'لا توجد عمليات تفتيش مكتملة ضمن عوامل التصفية المحددة.',

  /* rooms */
  roomsSubtitle: 'الغرف التي جرى تفتيشها ضمن الفترة المحددة',
  inspectionsCount: 'عمليات التفتيش',
  lastInspection: 'آخر تفتيش',
  missingItemsCount: 'مرات النقص',
  kitchenRoom: 'بها مطبخ',
  noRoomsTitle: 'لا توجد غرف',
  noRoomsText: 'لم تُسجَّل أي عملية تفتيش ضمن الفترة المحددة.',

  /* supervisors */
  supervisorsSubtitle: 'المشرفون الذين لديهم عمليات تفتيش مكتملة',
  totalCompleted: 'إجمالي المكتمل',
  inPeriod: 'ضمن الفترة',
  noSupervisorsTitle: 'لا يوجد مشرفون',
  noSupervisorsText: 'لم يُسجّل أي مشرف عملية تفتيش مكتملة بعد.',

  /* audit */
  auditSubtitle: 'التعديلات الإدارية على التقارير المكتملة',
  auditActionEdited: 'عدّل التقرير',
  auditNoFieldChange: 'لم تتغيّر أي قيمة.',
  auditScopeNote: (n: number) => `آخر ${n} تقرير مُعدَّل`,
  noAuditTitle: 'لا توجد تعديلات',
  noAuditText: 'لم تُعدَّل أي بيانات تقرير من قِبل الإدارة ضمن الفترة المحددة.',

  /* settings */
  settingsIntro: 'إعدادات حسابك وطريقة عرض واجهة الإدارة. لا تؤثر هذه الإعدادات على بيانات التفتيش.',
  settingsAccount: 'الحساب',
  settingsName: 'الاسم',
  settingsRole: 'الصلاحية',
  settingsRoleValue: 'مسؤول إدارة · صلاحية كاملة على التقارير',
  settingsSession: 'الجلسة',
  settingsSessionActive: 'نشطة',
  settingsSessionUntil: (until: string) => `حتى ${until}`,
  settingsDisplay: 'العرض واللغة',
  settingsDisplayDesc: 'واجهة الإدارة مصمَّمة للعربية والاتجاه من اليمين إلى اليسار.',
  settingsInterface: 'لغة الواجهة',
  settingsInterfaceValue: 'العربية · من اليمين إلى اليسار',
  settingsNumerals: 'الأرقام',
  settingsNumeralsValue: 'أرقام لاتينية (0–9)',
  settingsRegion: 'المنطقة والتوقيت',
  settingsRegionDesc: 'يُحتسب يوم العمل بتوقيت الرياض، وتُخزَّن الأوقات بالتوقيت العالمي.',
  settingsTimezone: 'المنطقة الزمنية',
  settingsTimezoneValue: 'الرياض · UTC+03:00',
  settingsBusinessDay: 'يوم العمل',
  settingsBusinessDayValue: 'من منتصف الليل إلى منتصف الليل بتوقيت الرياض',
  settingsApplication: 'التطبيق',
  settingsApi: 'واجهة الخدمة',
  settingsApiConnected: 'متصلة',
  settingsEnvironment: 'البيئة',
  settingsEnvironmentValue: 'الإنتاج',
  settingsSessionGroup: 'الجلسة',
  settingsSignOutTitle: 'تسجيل الخروج من هذا الجهاز',
  settingsSignOutNote: 'سيتم إنهاء الجلسة على هذا المتصفح فقط.',

  /* report */
  reportEyebrow: 'ROOM VERIFY',
  reportTitle: 'تقرير تفتيش غرفة',
  reportSubtitle: 'سلسلة الأدلة المرئية',
  verified: 'موثّق',
  inspectionId: 'معرّف التقرير',
  startTime: 'وقت البدء',
  completionTime: 'وقت الإنهاء',
  reportDate: 'التاريخ',
  businessDate: 'يوم العمل',
  roomItems: 'أغراض الغرفة',
  itemsComplete: 'أغراض الغرفة مكتملة',
  missingItemsTitle: 'الأغراض الناقصة',
  missingItemsCountLabel: (n: number) => `${n} من الأغراض`,
  otherItem: 'أخرى',
  print: 'طباعة',
  savePdf: 'حفظ PDF',
  preparingImages: 'جارٍ تحضير الصور…',
  imagesReady: (loaded: number, total: number) => `${loaded} / ${total} صورة`,
  capturedAt: 'الالتقاط',
  clockAdjusted: 'وقت الجهاز مُصحَّح',
  sectionRoomNumber: 'رقم الغرفة',
  sectionBathroom: 'دورة المياه',
  sectionKitchen: 'المطبخ',
  sectionRoom: 'الغرفة',
  photoCount: (n: number) => `${n} صورة`,
  evidenceHeading: 'الأدلة المصوّرة',

  /* lightbox */
  lightboxClose: 'إغلاق',
  lightboxPrev: 'الصورة السابقة',
  lightboxNext: 'الصورة التالية',
  lightboxOf: 'من',

  /* administrative edits */
  editedNotice: 'تم تعديل بيانات التقرير بواسطة الإدارة',
  editedBy: 'المُعدِّل',
  editedAt: 'وقت التعديل',
  editHistory: 'سجل التعديلات',
  showHistory: 'عرض السجل',
  hideHistory: 'إخفاء السجل',

  /* edit dialog */
  editReport: 'تصحيح بيانات التقرير',
  editHint:
    'يمكن تصحيح أخطاء الإدخال فقط. الصور وأوقات الالتقاط وبصمات الملفات واسم المشرف لا يمكن تعديلها.',
  itemsCompleteOption: 'كل الأغراض مكتملة',
  itemsMissingOption: 'يوجد أغراض ناقصة',
  selectMissingItems: 'اختر الأغراض الناقصة',
  otherItemPlaceholder: 'اكتب غرضًا ناقصًا آخر',
  saveChanges: 'حفظ التصحيح',
  saving: 'جارٍ الحفظ…',
  noChangesMade: 'لم يتم تغيير أي بيانات.',
  savedSuccessfully: 'تم حفظ التصحيح وتسجيله في سجل التدقيق.',

  /* errors */
  errInvalidCredentials: 'اسم المستخدم أو كلمة المرور غير صحيحة.',
  errForbidden: 'هذا الحساب لا يملك صلاحية الدخول إلى تطبيق الإدارة.',
  errSessionExpired: 'انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.',
  errNetwork: 'تعذّر الاتصال. تحقّق من الشبكة وحاول مرة أخرى.',
  errServer: 'حدثت مشكلة مؤقتة في الخادم. حاول بعد قليل.',
  errNotFound: 'التقرير غير موجود.',
  errInvalidRoom: 'رقم غرفة غير صالح (٣ أو ٤ أرقام).',
  errMissingItemsEmpty: 'اختر غرضًا ناقصًا واحدًا على الأقل، أو اكتب واحدًا في خانة أخرى.',
  errUnknown: 'حدث خطأ غير متوقع. حاول مرة أخرى.',
  errSectionFailed: 'تعذّر تحميل هذا القسم.',
  imageFailed: 'تعذّر تحميل الصورة',
} as const;

export type Copy = typeof ar;

export function errorMessage(code: string | null): string {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return ar.errInvalidCredentials;
    case 'FORBIDDEN':
      return ar.errForbidden;
    case 'SESSION_EXPIRED':
    case 'UNAUTHORIZED':
      return ar.errSessionExpired;
    case 'NETWORK':
      return ar.errNetwork;
    case 'NOT_FOUND':
      return ar.errNotFound;
    case 'INVALID_ROOM_NUMBER':
      return ar.errInvalidRoom;
    case 'MISSING_ITEMS_EMPTY':
    case 'MISSING_ITEMS_INVALID':
      return ar.errMissingItemsEmpty;
    case 'INTERNAL_ERROR':
      return ar.errServer;
    default:
      return ar.errUnknown;
  }
}
